<template>
  <div
    class="timetable-patient-cell"
    v-bind:class="{ ignore: isDrag }"
    v-bind:draggable="isDraggable"
    v-on:click.stop="dispatchClick"
    v-on:keydown="dispatchClick"
    v-on:dragend="onDragEnd"
    v-on:dragstart="onDragStart">
    <template v-if="isOneRowLayout">
      <div class="timetable-patient-cell__row">
        <span class="timetable-patient-cell__name">
          {{ patientName }}
        </span>
        <span class="timetable-patient-cell__time">
          {{ interval }}
        </span>
      </div>
    </template>
    <default-layout
      v-else-if="isDefaultLayout"
      v-bind:age-title="ageTitle"
      v-bind:can-create-invoice="canCreateInvoice"
      v-bind:interval="interval"
      v-bind:is-initial="isInitial"
      v-bind:labels="labels"
      v-bind:patient-name="patientName"
      v-bind:status="status"
      v-bind:invoice-status="invoiceStatus"
      v-bind:has-view-patient-invoices="hasViewPatientInvoices"
      v-bind:card-number="patientCardNumber"
      v-bind:patient-birth-date="patientBirthDate"
      v-bind:date="date"
      v-on:click.stop="onCreateInvoice"
    />
    <div
      class="timetable-patient-cell__hover"
      v-bind:style="{ backgroundColor: processedColor, height: processedHeight }"
      ref="cellPopupRef"
      v-if="canShowExpandedData"
    >
      <default-layout
        v-bind:age-title="ageTitle"
        v-bind:can-create-invoice="canCreateInvoice"
        v-bind:interval="interval"
        v-bind:is-initial="isInitial"
        v-bind:labels="labels"
        v-bind:patient-name="patientFullName"
        v-bind:status="status"
        v-bind:invoice-status="invoiceStatus"
        v-bind:has-view-patient-invoices="hasViewPatientInvoices"
        v-bind:card-number="patientCardNumber"
        v-bind:patient-birth-date="patientBirthDate"
        v-bind:date="date"
        v-on:click.stop="onCreateInvoice"
        v-on:open-invoices="onOpenInvoices"
      />
      <section
        class="timetable-patient-cell__info"
        v-if="availableExpandedInfo"
      >
        <div
          v-for="service in expandedSlot.serviceNames"
          v-bind:key="service"
          class="timetable-patient-cell__info__text"
        >
          {{ service }}
        </div>
        <div class="timetable-patient-cell__info__note">{{ note }}</div>
        <button
          v-if="canInitiateCalls"
          class="timetable-patient-cell__info__text callable"
          type="button"
          title="Начать звонок"
          v-on:click.stop="onInitiateCall">{{ expandedSlot.patientPhone }}</button>
        <div
          v-else
          class="timetable-patient-cell__info__text">{{ expandedSlot.patientPhone }}</div>
      </section>
      <section
        v-if="showLabels"
        class="timetable-patient-cell__info employee"
      >
        <div class="timetable-patient-cell__info__note">Особые отметки</div>
        <patient-labels v-bind:labels="expandedSlot.labelNames" />
      </section>
      <section
        v-if="showEmployeeName"
        class="timetable-patient-cell__info employee"
      >
        <div class="timetable-patient-cell__info__note">Врач</div>
        <div class="timetable-patient-cell__text">{{ expandedSlot.employeeName }}</div>
      </section>
    </div>
  </div>
</template>

<script>
import PatientLabels from '@/components/PatientLabels.vue';

const STATUS_ABSENT = 'absent';
const STATUS_CAME = 'came';
const STATUS_CONFIRM = 'confirm';

/**
 * @description Компонент статуса записи на прием.
 * @type {Vue}
 */
const appointmentStatus = {
  functional: true,
  props: {
    status: { required: true, type: String },
  },
  name: 'appointmentStatus',
  render(createElement, { props }) {
    const makeAppointmentStatusIcon = (type, title) => createElement(
      'span',
      { staticClass: 'timetable-patient-cell__status', class: type, attrs: { title } },
    );
    switch (props.status) {
      case STATUS_ABSENT: return makeAppointmentStatusIcon(STATUS_ABSENT, 'Неявка');
      case STATUS_CAME: return makeAppointmentStatusIcon(STATUS_CAME, 'Пациент пришел');
      case STATUS_CONFIRM: return makeAppointmentStatusIcon(STATUS_CONFIRM, 'Запись подтверждена');
      default: return createElement();
    }
  },
};

const LABEL_BLACK_LIST = 'black-list';
const LABEL_UNWANTED = 'unwanted';
const LABEL_VIP = 'vip';

/**
 * @description Компонент макета слота по умолчанию.
 * @type {Vue}
 */
const defaultLayout = {
  components: { appointmentStatus },
  functional: true,
  props: {
    ageTitle: { required: true, type: String },
    canCreateInvoice: { required: true, type: Boolean },
    interval: { required: true, type: String },
    isInitial: { required: true, type: Boolean },
    labels: { required: true, type: Array },
    patientName: { required: true, type: String },
    status: { default: '', type: String },
    invoiceStatus: { default: '', type: String },
    hasViewPatientInvoices: { default: () => false, type: Boolean },
    cardNumber: { default: () => '', type: String },
    patientBirthDate: { default: '', type: String },
    date: { default: '', type: String },
  },
  name: 'TimetablePatientDefaultLayout',
  render(createElement, { listeners, props }) {
    const interval = createElement('span', { staticClass: 'timetable-patient-cell__time' }, props.interval);
    const patientName = createElement('span', { staticClass: 'timetable-patient-cell__name' }, [
      props.patientName,
      props.ageTitle && ' ',
      props.ageTitle && createElement('span', props.ageTitle),
      props.cardNumber && ' ',
      props.cardNumber && createElement('span', props.cardNumber),
    ]);
    const date = new Date(props.date);
    const birthDate = new Date(props.patientBirthDate);
    const isPatientBirthdayToday = birthDate.getDate() === date.getDate()
      && birthDate.getMonth() === date.getMonth();
    const makeChip = (text) => createElement('span', { staticClass: 'timetable-patient-cell__chip' }, text);

    const makeBirthDateIcon = () => createElement(
      'img',
      {
        staticClass: 'timetable-patient-cell__birthdate',
        attrs: {
          title: 'День рождения',
          alt: 'День рождения',
          // eslint-disable-next-line global-require
          src: require('../assets/img/birthday.svg'),
        },
      },
    );
    const makeInvoiceCreation = () => createElement(
      'button',
      {
        staticClass: 'timetable-patient-cell__invoice',
        on: listeners,
        attrs: {
          title: 'Создать счет и перейти к нему',
          type: 'button',
        },
      },
    );
    const makeInvoiceStatus = () => createElement(
      'div',
      {
        staticClass: `timetable-patient-cell__invoice ${props.invoiceStatus}`,
        on: { click: () => listeners['open-invoices']() },
        attrs: {
          title: 'Перейти к счетам',
          type: 'button',
        },
      },
    );
    const makeRow = (children) => createElement(
      'div',
      { staticClass: 'timetable-patient-cell__row' },
      children,
    );
    return [
      makeRow([
        patientName,
        props.isInitial && makeChip('1'),
        props.canCreateInvoice && makeInvoiceCreation(),
        props.patientBirthDate && isPatientBirthdayToday && makeBirthDateIcon(),
        (
          props.invoiceStatus
          && !props.canCreateInvoice
          && props.hasViewPatientInvoices
        )
        && makeInvoiceStatus(),
      ]),
      makeRow([
        interval,
        props.status && createElement(appointmentStatus, { props: { status: props.status } }),
      ]),
    ];
  },
};

/**
 * @description Название события создания счета.
 * @type {string}
 */
const NAME_EVENT_CREATE_INVOICE = 'create-invoice';

/**
 * @description Название события открытия счетов.
 * @type {string}
 */
const NAME_EVENT_OPEN_INVOICES = 'open-invoices';

/**
 * @description Название события инициации звонка.
 * @type {string}
 */
const NAME_EVENT_INITIATE_CALL = 'initiate-call';

/**
 * @description Высота единичного шага.
 * @type {number}
 */
const STEP_HEIGHT = 53;

export default {
  components: { PatientLabels, defaultLayout },
  mounted() {
    this.cellPopup = this.$refs.cellPopupRef;
  },
  computed: {
    /**
     * @description Возраст.
     * @type {string}
     */
    ageTitle() {
      if (this.expandedSlot === null) {
        return '';
      }
      return this.expandedSlot.ageTitle;
    },
    /**
     * @description Наличие расширенной информации.
     * @type {bool}
     */
    availableExpandedInfo() {
      if (this.canShowExpandedData === false) {
        return false;
      }
      const serviceNamesIsNotEmpty = this.expandedSlot.serviceNames.length;
      const patientPhoneIsNotEmpty = this.expandedSlot.patientPhone !== '';
      const noteIsNotEmpty = this.note !== '';
      return serviceNamesIsNotEmpty || patientPhoneIsNotEmpty || noteIsNotEmpty;
    },
    /**
     * @description Показывать медкарту пациента.
     * @type {bool}
     */
    showPatientCard() {
      return this.expandedSlot && this.expandedSlot.medcard !== '';
    },
    /**
     * @description Показать метки
     * @param expandedSlot
     * @return {boolean}
     */
    showLabels({ expandedSlot }) {
      return expandedSlot && !!expandedSlot.labelNames.length;
    },
    /**
     * @description Статус записи на прием.
     * @returns {string}
     */
    status({ tags }) {
      return [STATUS_ABSENT, STATUS_CAME, STATUS_CONFIRM]
        .find((candidate) => tags.includes(candidate)) || '';
    },
    /**
     * @description Показывать расширенный попап.
     * @type {boolean}
     */
    canShowExpandedData() {
      return this.expandedSlot !== null && this.isDrag === false;
    },
    isOneRowLayout() {
      return this.tags.includes('one-row-layout');
    },
    isDefaultLayout() {
      return this.tags.includes('only-color-layout') === false;
    },
    /**
     * @description Список черт пациента.
     * @returns {string[]}
     */
    labels({ expandedSlot }) {
      if (expandedSlot === null) { return []; }
      return [LABEL_BLACK_LIST, LABEL_UNWANTED, LABEL_VIP]
        .filter((candidate) => expandedSlot.labelIds.includes(candidate));
    },
    invoiceStatus() {
      if (this.hasDebt) {
        return 'debt';
      }
      if (this.hasAdvance) {
        return 'advance';
      }
      return '';
    },
    isDraggable() {
      return !this.tags.includes(STATUS_CAME);
    },
    /**
     * @description Обработанный цвет фона расширенного попапа.
     * @type {string}
     */
    processedColor() {
      return this.color || '#eaeff3';
    },
    /**
     * @description Вычисленное значение для высоты расширенного попапа.
     * @type {string}
     */
    processedHeight() {
      if (this.cellPopup) {
        const selfHeight = this.ratioStep * STEP_HEIGHT;
        const cellPopupHeight = this.cellPopup.offsetHeight;
        if (selfHeight > cellPopupHeight) {
          return '100%';
        }
      }
      return '';
    },
  },
  data() {
    return {
      isDrag: false,
      cellPopup: null,
    };
  },
  methods: {
    /**
     * @description Обработать инициацию звонка.
     * @returns {void}
     */
    onInitiateCall() {
      this.$emit(NAME_EVENT_INITIATE_CALL, this.appointmentId);
    },
    /**
     * @description Обработать создание счета.
     * @returns {void}
     */
    onCreateInvoice() {
      this.$emit(NAME_EVENT_CREATE_INVOICE);
    },
    onDragEnd(ev) {
      this.isDrag = false;
      this.$emit('dragend', ev);
    },
    /**
     * @description Обработать начало перетаскивания.
     * @param {DragEvent} ev
     * @returns {void}
     */
    onDragStart(ev) {
      Object.assign(ev.dataTransfer, {
        ...ev.dataTransfer,
        effectAllowed: 'move',
        dropEffect: 'move',
      });
      ev.dataTransfer.setData('appointment-id', this.appointmentId);
      ev.dataTransfer.setData('ratio-step', this.ratioStep);
      this.$emit('dragstart', ev);
      setTimeout(() => { this.isDrag = true; }, 0);
    },
    /**
     * @description Обработать открытие счетов.
     * @returns {void}
     */
    onOpenInvoices() {
      this.$emit(NAME_EVENT_OPEN_INVOICES);
    },
    /**
     * @description Отправить событие клика.
     */
    dispatchClick() {
      this.$emit('click');
    },
  },
  props: {
    /**
     * @description Можно создать счет.
     * @type {boolean}
     */
    canCreateInvoice: { default: () => false, type: Boolean },
    /**
     * @description Может инициировать звонки.
     */
    canInitiateCalls: { default: () => false, type: Boolean },
    /**
     * @description Идентификатор записи на прием.
     * @type {number}
     */
    appointmentId: { required: true, type: Number },
    /**
     * @description Цвет заднего фона.
     * @type {string}
     */
    color: { type: String, default: '#eaeff3' },
    /**
     * @description Расширенный слот.
     * @type {any}
     */
    expandedSlot: { default: null, type: Object },
    /**
     * @description Размер относительно шага.
     * @type {Number}
     */
    ratioStep: { required: true, type: Number },
    /**
     * @description Интервал приема.
     * @type {string}
     */
    interval: { required: true, type: String },
    /**
     * @description Первичный прием.
     * @type {boolean}
     */
    isInitial: Boolean,
    /**
     * @description Примечание.
     * @type {string}
     */
    note: { type: String, default: '' },
    /**
     * @description Полное имя пацеинта.
     * @type {string}
     */
    patientFullName: { required: true, type: String },
    /**
     * @description Имя пациента.
     * @type {string}
     */
    patientName: { required: true, type: String },
    /**
     * @description Тэги.
     * @type {string[]}
     */
    tags: { required: true, type: Array },
    /**
     * @description Показывать имя сотрудника.
     * @type {boolean}
     */
    showEmployeeName: { type: Boolean, default: false },
    hasDebt: { type: Boolean, default: false },
    hasAdvance: { type: Boolean, default: false },
    /**
     * @description Может просматривать счета пациентов.
     * @type {boolean}
     */
    hasViewPatientInvoices: { type: Boolean, default: false },
    /**
     * @description Номер мед. карты пациента.
     * @type {String}
     */
    patientCardNumber: { type: String, default: () => '' },
    /**
     * @description Дата рождения пацеинта.
     * @type {string}
     */
    patientBirthDate: { type: String, default: '' },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    date: { type: String, default: '' },
  },
  name: 'TimetablePatientCell',
};
</script>

<style scoped lang="scss">

@keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.timetable-patient-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 2px 6px;
  height: 100%;
  &.ignore {
    pointer-events: none;
    .timetable-patient-cell__status {
      pointer-events: none;
    }
  }
  &__row {
    pointer-events: none;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 8px;
    width: 100%;
    justify-content: space-between;
  }
  &__text,
  &__name {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: #192a3e;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__chip {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-radius: 12px;
    font-weight: 400;
    font-size: 12px;
    line-height: 12px;
    padding: 2px 5px;
    color: #323c47;
  }
  &__labels {
    display: flex;
    flex: 1;
    margin-left: 7px;
    gap: 5px;
    & > * {
      stroke: gold;
    }
  }
  &__label {
    &.black-list {
      pointer-events: all;
      background-image: url('@/assets/img/black-list-grey.svg');
      background-position: center;
      width: 16px;
      height: 24px;
    }
    &.unwanted {
      pointer-events: all;
      background-image: url('@/assets/img/mad-grey.svg');
      background-position: center;
      width: 16px;
      height: 24px;
    }
  }
  &__time {
    color: #192a3e;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    white-space: nowrap;
  }
  &__status {
    align-self: flex-end;
    width: 20px;
    height: 15px;
    color: #192a3e;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    pointer-events: all;
    & > img {
      pointer-events: none;
    }
    &.absent {
      background-position: center;
      background-image: url('@/assets/img/status/absence-grey.svg');
    }
    &.came {
      background-position: center;
      background-image: url('@/assets/img/checked/all-checked-dark-top.svg');
    }
    &.confirm {
      background-position: center;
      background-image: url('@/assets/img/checked/checked-dark-top.svg');
    }
  }
  &__invoice {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    pointer-events: all;
    background-image: url("@/assets/img/switch/rub-grey.svg");
    background-position: center;
    background-size: contain;
    &:hover {
      background-image: url("@/assets/img/switch/rub-blue.svg");
    }
    &.debt {
      background-image: url("@/assets/img/switch/rub-red.svg");
    }
    &.advance {
      background-image: url("@/assets/img/switch/rub-green.svg");
    }
  }
  &__birthdate {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    pointer-events: all;
  }
  &:hover {
    border-end-end-radius: 0;
    border-end-start-radius: 0;
    .timetable-patient-cell__hover {
      visibility: visible;
      opacity: 1;
      animation-name: fadeInOpacity;
      animation-iteration-count: 1;
      animation-timing-function: ease-in;
      animation-duration: 250ms;
    }
  }
  &__hover {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    top: -1px;
    left: 0;
    z-index: 1;
    width: 100%;
    border-end-end-radius: var(--block-border-radius);
    border-end-start-radius: var(--block-border-radius);
    padding: 6px;
    box-shadow: 0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22);
    & .timetable-patient-cell__name {
      white-space: normal;
    }
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 0;
    &.employee {
      gap: 2px;
    }
    &__note,
    &__text {
      color: var(--reading-color);
      font-size: 12px;
      font-weight: 600;
      line-height: 17px;
      &.callable {
        text-align: left;
        color: var(--action-color);
        text-decoration: underline;
      }
    }
    &__note {
      font-weight: 400;
      opacity: 0.7;
    }
  }
}
</style>
